import { useEffect } from "react";
import { useLocation } from "react-router-dom";

import { useLocalStorage } from "@/hooks/use-local-storage";
import { cn } from "@/lib/utils";

import NavBar from "./NavBar";
import { SidebarContext } from "./useSidebarContext";

interface DashboardLayoutProps {
  children: React.ReactNode;
  containerClassName?: string;
}

const DashboardLayout = ({
  children,
  containerClassName,
}: DashboardLayoutProps) => {
  const location = useLocation();
  const [collapsed, setCollapsed] = useLocalStorage<boolean>(
    "sidebar:collapsed",
    false,
  );

  // Scroll to top on route change
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  // Toggle body class for drawer-collapsed state (used by CSS grid system)
  useEffect(() => {
    document.body.classList.toggle("drawer-collapsed", collapsed);
    return () => {
      document.body.classList.remove("drawer-collapsed");
    };
  }, [collapsed]);

  return (
    <SidebarContext.Provider value={{ collapsed, setCollapsed }}>
      <div className="min-h-screen flex flex-col bg-background text-foreground transition-colors duration-300">
        <NavBar />
        <main
          className={`pt-16 md:pt-0 flex-1 transition-all duration-300 ${
            collapsed ? "md:ml-20" : "md:ml-80"
          }`}
        >
          <div
            className={cn(
              "px-4 md:px-8 pb-8 pt-6 md:pt-24 mx-auto animate-fade-in",
              containerClassName,
            )}
          >
            {children}
          </div>
        </main>
      </div>
    </SidebarContext.Provider>
  );
};

export default DashboardLayout;
